import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Claim, ChatMessage, ChatReplyType, ChatReplyUi } from "@/types";
import { chatService } from "@/service/chatService";
import { RootState } from "../store";

interface ChatState {
  currentChat: ChatMessage[];
  currentChatToGpt: ChatMessage[];
  allClaims: Claim[];
  histories: Record<string, ChatMessage[]>;
  isLoading: boolean;
  isUploading: boolean;
  showSubmitClaim: boolean;
  showConfirmSubmitModal: boolean;
  policies: { id: string; product_title: string; policy_type: string }[];
  selectedPolicyId: string;
  claimId: string;
  isSidebarOpen: boolean;
  sidebarLoading: boolean;
  /** The claim was submitted from the chat itself (not the Submit button); the page then asks for feedback. */
  submittedFromChat: boolean;
}

const initialState: ChatState = {
  currentChat: [],
  currentChatToGpt: [],
  allClaims: [],
  histories: {},
  isLoading: false,
  isUploading: false,
  showSubmitClaim: false,
  showConfirmSubmitModal: false,
  policies: [],
  selectedPolicyId: "",
  claimId: "",
  isSidebarOpen: false,
  sidebarLoading: false,
  submittedFromChat: false,
};

const STARTING_TEXT = "Hi there! I'm Anaya, how can I help you?";

function welcomeMessage(): ChatMessage {
  return {
    role: "assistant",
    content: [{ type: "text", text: STARTING_TEXT }],
    timestamp: new Date().toISOString(),
  };
}

function messageHasStartingText(msg: ChatMessage): boolean {
  if (msg.role !== "assistant") return false;
  return msg.content?.some(
    (part) =>
      part.type === "text" &&
      String(part.text || "").includes("I'm Anaya, how can I help you?"),
  );
}

function withStartingText(messages: ChatMessage[]): ChatMessage[] {
  const visible = messages.filter((msg) => msg.role !== "system");
  if (visible.some(messageHasStartingText)) return visible;
  return [welcomeMessage(), ...visible];
}

function setClaimThread(
  state: ChatState,
  claimId: string,
  messages: ChatMessage[],
) {
  const thread = withStartingText(messages);
  state.claimId = claimId;
  state.currentChat = thread;
  state.currentChatToGpt = thread;
  state.histories[claimId] = thread;
}

function resetJourney(state: ChatState) {
  state.submittedFromChat = false;
}

const REPLY_TYPES: ChatReplyType[] = ["text", "button", "list", "error"];

/** Buttons or a list sent with a reply; anything malformed is dropped. */
function replyUi(raw: any): ChatReplyUi | undefined {
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.options)) return undefined;
  const options = raw.options
    .filter((o: any) => o && typeof o.id === "string" && typeof o.label === "string")
    .map((o: any) => ({ id: o.id, label: o.label, ...(typeof o.description === "string" ? { description: o.description } : {}) }));
  if (!options.length) return undefined;
  if (raw.type === "list") {
    return { type: "list", title: String(raw.title || ""), description: String(raw.description || ""), button: String(raw.button || "Choose"), options };
  }
  return raw.type === "buttons" ? { type: "buttons", title: String(raw.title || ""), options } : undefined;
}

/** An assistant message from a chat or submit response: the WhatsApp-shaped `reply` (LangGraph) or the legacy `message`. */
function assistantReply(payload: any): ChatMessage | null {
  const text =
    typeof payload?.reply === "string" ? payload.reply
    : typeof payload?.message === "string" ? payload.message
    : "";
  if (!text.trim()) return null;
  const ui = replyUi(payload?.ui);
  const messageType: ChatReplyType = REPLY_TYPES.includes(payload?.messageType) ? payload.messageType : "text";
  return {
    role: "assistant",
    content: [{ type: "text", text }],
    timestamp: new Date().toISOString(),
    ...(payload?.messageType || ui ? { messageType } : {}),
    ...(ui ? { ui } : {}),
  };
}

function pushAssistant(state: ChatState, msg: ChatMessage) {
  state.currentChat.push(msg);
  state.currentChatToGpt.push(msg);
  if (state.claimId) {
    if (!state.histories[state.claimId]) state.histories[state.claimId] = [];
    state.histories[state.claimId].push(msg);
  }
}

function applyWelcomeChat(state: ChatState, claimId: string) {
  setClaimThread(state, claimId, []);
  state.showSubmitClaim = false;
}

function normalizeRole(role: unknown): ChatMessage["role"] | null {
  const value = String(role ?? "").trim().toLowerCase();
  if (["user", "customer", "human"].includes(value)) return "user";
  if (
    ["assistant", "bot", "ai", "anaya", "model", "chatbot", "system_assistant"].includes(
      value,
    )
  ) {
    return "assistant";
  }
  if (value === "system") return "system";
  return null;
}

function normalizeContent(raw: unknown): ChatMessage["content"] {
  if (typeof raw === "string" && raw.trim()) {
    return [{ type: "text", text: raw }];
  }
  if (Array.isArray(raw)) {
    return raw.flatMap((part) => normalizeContent(part));
  }
  if (!raw || typeof raw !== "object") return [];

  const record = raw as Record<string, any>;
  if (record.type === "image_url" || record.image_url?.url) {
    const url = record.image_url?.url || record.url;
    return url ? [{ type: "image_url", image_url: { url } }] : [];
  }
  if (record.type === "video_url" || record.video_url?.url) {
    const url = record.video_url?.url || record.url;
    return url ? [{ type: "video_url", video_url: { url } }] : [];
  }
  if (record.type === "pdf_url" || record.pdf_url?.url) {
    return [
      {
        type: "pdf_url",
        pdf_url: {
          url: record.pdf_url?.url || record.url,
          file_name: record.pdf_url?.file_name || record.file_name,
        },
      },
    ];
  }
  if (typeof record.text === "string" && record.text.trim()) {
    return [{ type: "text", text: record.text }];
  }
  if (typeof record.message === "string" && record.message.trim()) {
    return [{ type: "text", text: record.message }];
  }
  if (typeof record.content === "string" && record.content.trim()) {
    return [{ type: "text", text: record.content }];
  }
  return [];
}

function normalizeChatMessage(raw: unknown): ChatMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, any>;
  const role = normalizeRole(record.role ?? record.sender ?? record.from ?? record.author);
  const content = normalizeContent(
    record.content ?? record.message ?? record.text ?? record.msg,
  );
  if (!role || content.length === 0) return null;
  const ui = replyUi(record.ui);
  return {
    role,
    content,
    timestamp: record.timestamp || record.created_at,
    msg_id: record.msg_id,
    ...(REPLY_TYPES.includes(record.messageType) ? { messageType: record.messageType } : {}),
    ...(ui ? { ui } : {}),
  };
}

function extractChatHistory(raw: any): ChatMessage[] {
  const sources = [
    raw?.messages,
    raw?.existing_chat,
    raw?.chat,
    raw?.chat_history,
    raw?.history,
    raw?.conversation,
    raw?.messagesArr,
    raw?.claim?.messages,
    raw?.claim?.existing_chat,
    raw?.claim?.chat_history,
    raw?.claim?.chat,
    raw?.chat_with_ai,
    raw?.claim?.chat_with_ai,
    raw?.existing_chat,
  ];

  for (const source of sources) {
    if (!Array.isArray(source) || source.length === 0) continue;
    const messages = source
      .map((entry) => normalizeChatMessage(entry))
      .filter((entry): entry is ChatMessage => Boolean(entry));
    if (messages.length > 0) return messages;
  }

  return [];
}

// ─── HELPER: Check if claim has enough info to submit ────────────────────────
const checkClaimReadiness = (state: ChatState): boolean => {
  const { currentChat } = state;

  const userMessages = currentChat.filter((msg) => msg.role === "user");

  // Count images uploaded by user
  let imageCount = 0;
  userMessages.forEach((msg) => {
    msg.content.forEach((c) => {
      if (c.type === "image_url") {
        imageCount++;
      }
    });
  });

  // Count text messages from user (messages that contain actual text)
  let textMessageCount = 0;
  userMessages.forEach((msg) => {
    const hasText = msg.content.some(
      (c) =>
        c.type === "text" &&
        typeof c.text === "string" &&
        c.text.trim().length > 0,
    );
    if (hasText) {
      textMessageCount++;
    }
  });

  // ✅ First check: Need at least 2 images AND 3 text messages
  if (imageCount < 2 || textMessageCount < 3) {
    return false;
  }

  // ✅ Second check: Only hide if last message mentions mixed/wrong images
  const lastAssistantMessage = [...currentChat]
    .reverse()
    .find((msg) => msg.role === "assistant");

  if (lastAssistantMessage) {
    const lastTextContent = lastAssistantMessage.content.find(
      (c) => c.type === "text",
    );
    const lastText = (lastTextContent?.text || "").toLowerCase();

    // Only check for mixed/wrong image issues
    const wrongImageKeywords = [
      "mix-up",
      "mix up",
      "mixup",
      "wrong image",
      "wrong photo",
      "wrong picture",
      "incorrect image",
      "incorrect photo",
      "not of the",
      "not the correct",
      "not the right",
      "different product",
      "wrong product",
      "doesn't match",
      "does not match",
      "not related",
      "unrelated image",
      "unrelated photo",
    ];

    const hasWrongImageIssue = wrongImageKeywords.some((phrase) =>
      lastText.includes(phrase),
    );

    if (hasWrongImageIssue) {
      return false;
    }
  }

  // ✅ All checks passed - show submit button
  return true;
};

/**
 * Fetch all available policies from backend.
 */
export const fetchPolicies = createAsyncThunk(
  "chat/fetchPolicies",
  async () => {
    return await chatService.fetchPolicies();
  },
);

/**
 * Fetch existing claims from backend.
 */
export const fetchClaims = createAsyncThunk("chat/fetchClaims", async () => {
  return await chatService.fetchClaims();
});

/**
 * Create a new claim by sending only { policyId }.
 */
export const createClaim = createAsyncThunk(
  "chat/createClaim",
  async (payload: { policyId: string }, { rejectWithValue }) => {
    try {
      return await chatService.createClaim(payload);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || "Could not create claim." },
      );
    }
  },
);

/**
 * Reopen an existing, unsubmitted claim.
 */
export const openClaim = createAsyncThunk(
  "chat/openClaim",
  async (payload: { policyId: string; claimId?: string }, { rejectWithValue }) => {
    try {
      return await chatService.openClaim(payload);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ?? {
          message: error?.message || "Could not open claim.",
        },
      );
    }
  },
);

// check for care
export const checkForFreeCare = createAsyncThunk(
  "chat/checkFreeCare",
  async (payload: { policyId: string }) => {
    const raw = await chatService.checkForCare(payload);
    return raw;
  },
);

/**
 * Send a chat message to the backend for a given claim.
 */
// FIX: We now use getState() to get the FRESH history.
// We no longer accept currentChatToGpt as an argument to avoid stale state bugs.
export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (args: { message: ChatMessage; claimId: string }, { getState }) => {
    const state = getState() as RootState;
    // The reducer 'addLocalUserMessage' has already run, so currentChatToGpt in state is fresh.
    const freshChatToGpt = state.chat.currentChatToGpt;

    return await chatService.sendMessage(
      args.message,
      args.claimId,
      freshChatToGpt,
    );
  },
);

/**
 * Upload image evidence for an existing claim.
 */
export const uploadEvidence = createAsyncThunk(
  "chat/uploadEvidence",
  async (args: { formData: FormData; claimId: string }, { rejectWithValue }) => {
    try {
      return await chatService.uploadEvidence(args.formData);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || "Could not upload the files." },
      );
    }
  },
);

/**
 * Submit the claim for evaluation.
 */
export const submitClaim = createAsyncThunk(
  "chat/submitClaim",
  async (claimId: string, { rejectWithValue }) => {
    try {
      return await chatService.submitClaim(claimId);
    } catch (error: any) {
      // e.g. 409 from the LangGraph chat: the claim isn't ready, with what's still needed.
      return rejectWithValue(
        error?.response?.data ?? { message: error?.message || "Could not submit the claim." },
      );
    }
  },
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setCurrentChat: (state, action: PayloadAction<ChatMessage[]>) => {
      state.currentChat = action.payload;
    },
    setCurrentChatToGpt: (state, action: PayloadAction<ChatMessage[]>) => {
      state.currentChatToGpt = action.payload;
    },
    // NEW: Safely append a user message without overwriting the whole list
    addLocalUserMessage: (state, action: PayloadAction<ChatMessage>) => {
      const msg = action.payload;
      state.currentChat.push(msg);
      state.currentChatToGpt.push(msg);

      // Also update history to persist across navigation
      if (state.claimId) {
        if (!state.histories[state.claimId])
          state.histories[state.claimId] = [];
        state.histories[state.claimId].push(msg);
      }
    },
    setSelectedPolicyId: (state, action: PayloadAction<string>) => {
      state.selectedPolicyId = action.payload;
    },
    setClaimId: (state, action: PayloadAction<string>) => {
      const newId = String(action.payload || "").trim();
      if (!newId) return;

      // Same claim already on screen — don't rebuild the thread (causes flicker).
      if (state.claimId === newId && state.currentChat.length > 0) {
        return;
      }

      resetJourney(state);
      const saved = state.histories[newId];
      if (saved && saved.length) {
        setClaimThread(state, newId, saved);
      } else {
        applyWelcomeChat(state, newId);
      }

      state.showSubmitClaim = checkClaimReadiness(state);
    },
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    updateShowSubmitClaim: (state) => {
      state.showSubmitClaim = checkClaimReadiness(state);
    },
    // ─── Modal Actions ─────────────────────────────────────────────
    openConfirmSubmitModal: (state) => {
      state.showConfirmSubmitModal = true;
    },
    closeConfirmSubmitModal: (state) => {
      state.showConfirmSubmitModal = false;
    },
    // ─── Clear Chat Action ─────────────────────────────────────────
    clearSubmittedFromChat: (state) => {
      state.submittedFromChat = false;
    },
    clearCurrentChat: (state) => {
      resetJourney(state);
      state.currentChat = [];
      state.currentChatToGpt = [];
      state.claimId = "";
      state.selectedPolicyId = "";
      state.showSubmitClaim = false;
      state.showConfirmSubmitModal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ─── fetchPolicies ───────────────────────────────────────────
      .addCase(fetchPolicies.pending, () => {
        // Don't toggle isLoading — that flag drives the in-chat typing indicator.
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.policies = action.payload;
      })
      .addCase(fetchPolicies.rejected, () => {})

      // ─── fetchClaims ──────────────────────────────────────────────
      .addCase(fetchClaims.pending, (state) => {
        state.sidebarLoading = true;
      })
      .addCase(fetchClaims.fulfilled, (state, action) => {
        console.log("fetchClaims payload:", action.payload);
        state.allClaims = action.payload.map((raw: any) => {
          const protection =
            raw.type === "shipping"
              ? "Shipping Protection"
              : "Product Protection";
          return {
            id: raw.id,
            policyId: raw.policyId ?? raw.policy_id ?? "",
            productName: raw.product_title,
            filedAt: raw.created_at,
            claimType: raw.type,
            description: raw.description ?? "",
            amount: raw.amount ?? 0,
            status: raw.status as
              | "approved"
              | "rejected"
              | "under_review"
              | "pending",
            mode: raw.mode,
            submitted:
              typeof raw.submitted === "boolean" ? raw.submitted : undefined,
            evidence: raw.evidence ?? [],
            protection,
            resolution: raw.resolution ?? undefined,
            timeline: raw.timeline ?? undefined,
            imageUrl: raw.image_url ?? undefined,
          } as Claim;
        });
        state.sidebarLoading = false;
      })
      .addCase(fetchClaims.rejected, (state) => {
        state.sidebarLoading = false;
      })

      // ─── createClaim ──────────────────────────────────────────────
      .addCase(createClaim.pending, (state, action) => {
        console.log("Creating claim with policyId=", action.meta.arg.policyId);
        state.isLoading = true;
      })
      .addCase(createClaim.fulfilled, (state, action) => {
        const raw = action.payload as any;
        console.log("Backend returned new claim (raw):", raw);

        const protection =
          raw.type === "shipping"
            ? "Shipping Protection"
            : "Product Protection";

        const policy = state.policies.find((p) => p.id === raw.policy_id);
        const productName = policy ? policy.product_title : raw.product_title;

        const newClaim: Claim = {
          id: raw.id,
          policyId: raw.policy_id ?? "",
          productName,
          filedAt: raw.created_at,
          claimType: raw.type,
          description: raw.description ?? "",
          amount: raw.amount ?? 0,
          status: raw.status as
            | "approved"
            | "rejected"
            | "under_review"
            | "pending",
          evidence: raw.evidence ?? [],
          protection,
          resolution: raw.resolution ?? undefined,
          timeline: raw.timeline ?? undefined,
          imageUrl: raw.image_url ?? undefined,
        };

        state.isLoading = false;
        state.allClaims.push(newClaim);
        if (newClaim.id) applyWelcomeChat(state, newClaim.id);
      })
      .addCase(createClaim.rejected, (state) => {
        state.isLoading = false;
      })

      // ─── openClaim ────────────────────────────────────────────────
      .addCase(openClaim.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(openClaim.fulfilled, (state, action) => {
        const raw = action.payload as any;
        const claimRaw = raw?.claim ?? raw;
        const id = String(
          claimRaw?.id ?? claimRaw?.claim_id ?? action.meta.arg.claimId ?? "",
        ).trim();

        state.isLoading = false;
        if (!id) return;

        const messages = extractChatHistory(raw);
        const fallback = state.histories[id] || [];
        resetJourney(state);

        if (messages.length > 0) {
          setClaimThread(state, id, messages);
        } else if (fallback.length > 0) {
          setClaimThread(state, id, fallback);
        } else {
          applyWelcomeChat(state, id);
        }

        state.showSubmitClaim = checkClaimReadiness(state);
      })
      .addCase(openClaim.rejected, (state) => {
        state.isLoading = false;
      })

      // ─── check free care ──────────────────────────────────────────────
      .addCase(checkForFreeCare.pending, (state, action) => {
        console.log("Creating claim with policyId=", action.meta.arg.policyId);
        state.isLoading = true;
      })
      .addCase(checkForFreeCare.fulfilled, (state, action) => {
        state.isLoading = false;
        const raw = action.payload;

        // Find product name for the dynamic text
        // const policy = state.policies.find((p) => p.id === raw.policy_id);
        // const productName = policy
        //   ? policy.product_title
        //   : raw.product_title || "your product";

        // // 1. Initial Visible Message
        // const assistantMsg: ChatMessage = {
        //   role: "assistant",
        //   content: [
        //     {
        //       type: "text",
        //       text: `Great! You're eligible for Free Annual Professional Care\nThis service helps maintain your ${productName} in top condition — ensuring better performance, safety & reliability.\nHere’s what your free service includes:\n• Full performance check\n• Safety inspection\n• Professional cleaning\n• Internal & external component review\nBefore I guide you, one quick thing:\nIs your product currently functional?`,
        //     },
        //   ],
        // };

        // // 2. Hidden System Prompt to force the "Second Response" behavior
        // // This ensures when the user types "Yes", the AI knows to give the phone number
        // const systemInstruction: ChatMessage = {
        //   role: "system",
        //   content: [
        //     {
        //       type: "text",
        //       text: `Context: User is eligible for Free Annual Service for ${productName}. I just asked if the product is functional.
        //       If the user says "Yes" or confirms it works:
        //       Reply exactly like this: "Perfect! Since your ${productName} is functional, you can directly schedule your free maintenance service.
        //       Please contact our Service Team at:
        //       📞 +91-22-69620766
        //       They'll help you book your pickup and service slot.
        //       Let me know if you need help with anything else!"`,
        //     },
        //   ],
        // };

        // Set state for the new conversation
        state.claimId = raw.id;
        // state.currentChat = [assistantMsg];
        // state.currentChatToGpt = [systemInstruction, assistantMsg];
        // state.histories[raw.id] = [assistantMsg];
        // state.showSubmitClaim = false;
      })
      .addCase(checkForFreeCare.rejected, (state) => {
        state.isLoading = false;
      })

      // ─── sendMessage ──────────────────────────────────────────────
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const payload = action.payload;
        // An 'error' reply (e.g. the assistant is unavailable) is shown too, styled as an error.
        const reply = payload?.success || payload?.messageType === "error" ? assistantReply(payload) : null;
        if (reply) pushAssistant(state, reply);
        // The customer submitted from the chat ("Submit claim"); the page follows up as after the Submit button.
        if (payload?.success && payload?.completed === true) state.submittedFromChat = true;
        state.isLoading = false;

        // Auto-update showSubmitClaim after each message
        state.showSubmitClaim = checkClaimReadiness(state);
      })
      .addCase(sendMessage.rejected, (state) => {
        state.isLoading = false;
      })

      // ─── uploadEvidence ───────────────────────────────────────────
      .addCase(uploadEvidence.pending, (state) => {
        state.isUploading = true;
      })
      .addCase(uploadEvidence.fulfilled, (state) => {
        state.isUploading = false;
        state.showSubmitClaim = checkClaimReadiness(state);
      })

      .addCase(uploadEvidence.rejected, (state) => {
        state.isUploading = false;
      })

      // ─── submitClaim ──────────────────────────────────────────────
      .addCase(submitClaim.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(submitClaim.fulfilled, (state, action) => {
        // Legacy: { message: ChatMessage }. LangGraph: the WhatsApp-shaped { reply, messageType, ui }.
        const legacy = action.payload?.message;
        const aiMsg: ChatMessage | null =
          legacy && typeof legacy === "object"
            ? { ...legacy, timestamp: new Date().toISOString() }
            : assistantReply(action.payload);
        if (aiMsg) pushAssistant(state, aiMsg);
        state.isLoading = false;

        // Hide submit button after claim is submitted
        state.showSubmitClaim = false;
        // Close modal after submission
        state.showConfirmSubmitModal = false;
      })
      .addCase(submitClaim.rejected, (state, action) => {
        state.isLoading = false;
        state.showConfirmSubmitModal = false;
        // Show why it wasn't submitted (the backend saved the same reply in the claim's chat).
        const reply = assistantReply(action.payload);
        if (reply) pushAssistant(state, reply);
        state.showSubmitClaim = checkClaimReadiness(state);
      });
  },
});

export const {
  setCurrentChat,
  setCurrentChatToGpt,
  addLocalUserMessage,
  setSelectedPolicyId,
  setClaimId,
  toggleSidebar,
  updateShowSubmitClaim,
  openConfirmSubmitModal,
  closeConfirmSubmitModal,
  clearCurrentChat, // ← Added export
  clearSubmittedFromChat,
} = chatSlice.actions;

export default chatSlice.reducer;
